import { Box, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import CopyField from '~/components/Fields/CopyField';
import { LabelText } from '~/components/Fields/LabeledValue';
import { useRoiStatistics } from '~/hooks/structure-set/useRoiStatistics';
import { type AxisValue, roiStatRows } from './roiStatRows';

interface ContourStatisticsProps {
    roiNumber: number;
}

/**
 * Renders a vector statistic as per-axis (X/Y/Z) components so the coordinate
 * frame is explicit. The axis letters share the turquoise accent and the
 * numbers sit in a fixed-width, right-aligned column so the center-of-mass and
 * bounding-box rows line up; the value stays copyable as an axis-labeled
 * string.
 */
function AxisTripletValue({ axes }: { axes: readonly AxisValue[] }) {
    const copyText = axes
        .map(
            (component) => `${component.axis.toUpperCase()} ${component.value}`
        )
        .join(', ');
    return (
        <CopyField size={11} noWrap copyText={copyText}>
            <Box
                display="inline-flex"
                alignItems="baseline"
                gap={1}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {axes.map((component) => (
                    <Box
                        key={component.axis}
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'baseline',
                            fontSize: 11,
                        }}
                    >
                        {/* All axis letters share the turquoise accent; the
                            value stays in the default high-contrast text color
                            so the clinical number is easy to read. */}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 400,
                                mr: 0.25,
                                color: 'primary.main',
                            }}
                        >
                            {component.axis.toUpperCase()}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                minWidth: '5ch',
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {component.value}
                        </Box>
                    </Box>
                ))}
            </Box>
        </CopyField>
    );
}

/**
 * "Contour Statistics" subsection of the structure Details popover: surface
 * area, sphericity, center of mass, and bounding box, computed from the ROI's
 * SDF (volume is shown on the docked panel). Laid out as a two-column grid
 * (label, value) so it matches the Information section above it — scalars show
 * a single number, center of mass and bounding box show their per-axis (X/Y/Z)
 * components. Renders nothing when stats are unavailable (point ROIs, empty
 * ROIs, or no environment yet).
 */
export default function ContourStatistics({
    roiNumber,
}: ContourStatisticsProps) {
    const stats = useRoiStatistics(roiNumber);
    if (!stats) {
        return null;
    }

    return (
        <Stack spacing={0.5} mt={1} data-testid="contour-statistics">
            <Typography
                variant="caption"
                sx={{ fontSize: 13, fontWeight: 500 }}
            >
                <FormattedMessage id="roi.stats.title" />
            </Typography>
            <Box
                display="grid"
                gridTemplateColumns="auto 1fr"
                gap="3px 6px"
                alignItems="baseline"
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: 11,
                        lineHeight: 1.2,
                    },
                }}
            >
                {roiStatRows(stats).map((row) => (
                    <Fragment key={row.labelId}>
                        <LabelText>
                            <FormattedMessage id={row.labelId} />
                        </LabelText>
                        {row.kind === 'axes' ? (
                            <AxisTripletValue axes={row.axes} />
                        ) : (
                            <CopyField label={row.value} size={11} noWrap />
                        )}
                    </Fragment>
                ))}
            </Box>
        </Stack>
    );
}
